import { useState } from 'react';

import { type MockNotificationPrefs } from '@/lib/mock-data';
import { Button, Card, CardTitle, toast } from '@/components/ui';

/**
 * AccountNotifications — `/account/notifications`. 4 toggles de preferencias
 * (emailMarketing/emailTransactional/smsReminders/pushApp). El estado se
 * mantiene local; la persistencia real contra el perfil del usuario se
 * integra en un sprint posterior.
 */
type PrefKey = keyof MockNotificationPrefs;

const DEFAULT_PREFS: MockNotificationPrefs = {
  emailMarketing: true,
  emailTransactional: true,
  smsReminders: false,
  pushApp: true,
};

const PREF_LABELS: Record<PrefKey, { title: string; description: string }> = {
  emailMarketing: {
    title: 'Marketing por email',
    description: 'Promociones, lanzamientos y experiencias destacadas. Frecuencia máxima semanal.',
  },
  emailTransactional: {
    title: 'Email transaccional',
    description:
      'Confirmaciones de compra, QR de entrada, recordatorios de evento. NO se puede desactivar para órdenes confirmadas.',
  },
  smsReminders: {
    title: 'Recordatorios por SMS',
    description: 'Aviso 24h antes de cada evento con QR + dirección.',
  },
  pushApp: {
    title: 'Push notifications (app)',
    description: 'Cuando usás la app móvil, recibís push sobre tus eventos y promos relevantes.',
  },
};

export function AccountNotifications(): JSX.Element {
  const [prefs, setPrefs] = useState<MockNotificationPrefs>(DEFAULT_PREFS);
  const [pending, setPending] = useState(false);

  function toggle(key: PrefKey): void {
    if (key === 'emailTransactional') {
      // Bloqueado: email transactional siempre on para orders confirmadas
      toast.info({
        title: 'No se puede desactivar',
        description: 'El email transaccional es obligatorio para confirmaciones de orden.',
      });
      return;
    }
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  }

  async function handleSave(): Promise<void> {
    setPending(true);
    try {
      // Mock save: simulamos round-trip server.
      await new Promise((r) => setTimeout(r, 600));
      toast.success({ title: 'Preferencias guardadas' });
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <Card variant="default" padded>
        <CardTitle>Preferencias de notificaciones</CardTitle>
        <p className="mt-2 text-sm text-fc-muted">
          Elegí cómo querés que te contactemos. Podés cambiarlo cuando quieras.
        </p>
      </Card>

      <ul className="flex flex-col gap-4">
        {(Object.keys(PREF_LABELS) as PrefKey[]).map((key) => (
          <li key={key}>
            <PrefToggleCard
              labelTitle={PREF_LABELS[key].title}
              labelDescription={PREF_LABELS[key].description}
              checked={prefs[key]}
              onChange={() => toggle(key)}
              forcedOn={key === 'emailTransactional'}
            />
          </li>
        ))}
      </ul>

      <div>
        <Button onClick={handleSave} disabled={pending}>
          {pending ? 'Guardando…' : 'Guardar preferencias'}
        </Button>
      </div>
    </div>
  );
}

function PrefToggleCard({
  labelTitle,
  labelDescription,
  checked,
  onChange,
  forcedOn,
}: {
  labelTitle: string;
  labelDescription: string;
  checked: boolean;
  onChange: () => void;
  forcedOn?: boolean;
}): JSX.Element {
  return (
    <Card variant="default" padded>
      <label className="flex cursor-pointer items-start gap-4">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={forcedOn}
          className="mt-1 h-5 w-5 flex-none cursor-pointer rounded border-neutral-300 text-primary focus:ring-2 focus:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-60"
        />
        <span className="flex-1">
          <span className="block font-medium text-fc-ink">{labelTitle}</span>
          <span className="mt-1 block text-sm text-fc-muted">{labelDescription}</span>
        </span>
      </label>
    </Card>
  );
}
